/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

/* eslint-disable react/no-array-index-key */

import Attachment16 from '@carbon/icons/es/attachment/16.js';
import { carbonIconToReact } from '../utils/carbonIcon';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from '../hooks/useIntl';

import {
  BusEventFeedback,
  BusEventType,
  FeedbackInteractionType,
} from '../../types/events/eventBusTypes';
import FeedbackButtons from '@carbon/ai-chat-components/es/react/feedback-buttons.js';
import Feedback, {
  type FeedbackInitialValues,
  type FeedbackSubmitDetails,
} from '@carbon/ai-chat-components/es/react/feedback.js';
import prefix from '@carbon/ai-chat-components/es/globals/settings.js';
import { SystemMessage } from '../components/responseTypes/message/SystemMessage';
import { ConnectToHumanAgent } from './responseTypes/humanAgent/ConnectToHumanAgent';
import { AudioComponent } from '../components/responseTypes/media/AudioComponent';
import { ButtonItemComponent } from '../components/responseTypes/button/ButtonItemComponent';
import { CardItemComponent } from '../components/responseTypes/card/CardItemComponent';
import { PreviewCardComponent } from './responseTypes/previewCard/PreviewCardComponent';
import { CarouselItemComponent } from '../components/responseTypes/card/CarouselItemComponent';
import { ConversationalSearch } from './responseTypes/conversationalSearch/ConversationalSearch';
import UserDefinedResponse from '../components/responseTypes/userDefined/UserDefinedResponse';
import CustomFooterSlot from './responseTypes/custom/CustomFooterSlot';
import { DatePickerComponent } from '../components/responseTypes/datePicker/DatePickerComponent';
import InlineError from '../components/responseTypes/error/InlineError';
import { GridItemComponent } from './responseTypes/grid/GridItemComponent';
import { IFrameMessage } from './responseTypes/iframe/IFrameMessage';
import { Image } from './responseTypes/image/Image';
import { OptionComponent } from '../components/responseTypes/options/OptionComponent';
import { MarkdownWithErrorHandling } from '../components/helpers/MarkdownWithErrorHandling/MarkdownWithErrorHandling';
import { VideoComponent } from '../components/responseTypes/media/VideoComponent';
import { useSelector } from '../hooks/useSelector';
import { shallowEqual } from '../store/appStore';
import { AppState } from '../../types/state/AppState';
import { useUUID } from '../hooks/useUUID';
import actions from '../store/actions';
import {
  LocalMessageItem,
  MessageErrorState,
} from '../../types/messaging/LocalMessageItem';
import { MessageTypeComponentProps } from '../../types/messaging/MessageTypeComponentProps';
import {
  getMediaDimensions,
  getRequestBubbleText,
  hasRequestBubbleContent,
  isRequest,
  isResponse,
  renderAsUserDefinedMessage,
} from '../utils/messageUtils';
import { parseUnknownDataToMarkdown } from '../utils/parseUnknownDataToMarkdown';
import ChainOfThought from '@carbon/ai-chat-components/es/react/chain-of-thought.js';
import ChainOfThoughtStep from '@carbon/ai-chat-components/es/react/chain-of-thought-step.js';
import ChainOfThoughtToggle from '@carbon/ai-chat-components/es/react/chain-of-thought-toggle.js';
import ToolCallData from '@carbon/ai-chat-components/es/react/tool-call-data.js';
import {
  AudioItem,
  ButtonItem,
  CardItem,
  CarouselItem,
  ConnectToHumanAgentItem,
  ConversationalSearchItem,
  DateItem,
  GridItem,
  IFrameItem,
  IFrameItemDisplayOption,
  ImageItem,
  InlineErrorItem,
  InternalMessageRequestType,
  Message,
  MessageHistoryFeedback,
  MessageInputType,
  MessageRequest,
  MessageResponse,
  MessageResponseHistory,
  MessageResponseTypes,
  OptionItem,
  SystemMessageItem,
  TextItem,
  UserType,
  VideoItem,
  PreviewCardItem,
} from '../../types/messaging/Messages';
import { MarkdownWithDefaults } from '../components/helpers/MarkdownWithDefaults/MarkdownWithDefaults';
import { MessageRichUserContent } from './MessageRichUserContent';
import type { CDSAIChatChainOfThought } from '@carbon/ai-chat-components/es/components/chain-of-thought/src/chain-of-thought.js';
import Carousel from '@carbon/ai-chat-components/es/react/carousel.js';

/**
 * The exact language-pack strings MessageTypeComponent renders. Selected as a
 * narrow bag (with `shallowEqual`) so the per-message component re-renders only
 * when one of these strings changes — not on any language-pack edit.
 */
const selectMessageTypeStrings = (state: AppState) => ({
  carousel_nextNavButton: state.languagePack.carousel_nextNavButton,
  carousel_prevNavButton: state.languagePack.carousel_prevNavButton,
  chainOfThought_explainabilityLabel:
    state.languagePack.chainOfThought_explainabilityLabel,
  chainOfThought_inputLabel: state.languagePack.chainOfThought_inputLabel,
  chainOfThought_outputLabel: state.languagePack.chainOfThought_outputLabel,
  chainOfThought_statusFailedLabel:
    state.languagePack.chainOfThought_statusFailedLabel,
  chainOfThought_statusProcessingLabel:
    state.languagePack.chainOfThought_statusProcessingLabel,
  chainOfThought_statusSucceededLabel:
    state.languagePack.chainOfThought_statusSucceededLabel,
  chainOfThought_toolLabel: state.languagePack.chainOfThought_toolLabel,
  errors_imageSource: state.languagePack.errors_imageSource,
  feedback_categoriesLabel: state.languagePack.feedback_categoriesLabel,
  feedback_defaultPlaceholder: state.languagePack.feedback_defaultPlaceholder,
  feedback_defaultPrompt: state.languagePack.feedback_defaultPrompt,
  feedback_defaultTitle: state.languagePack.feedback_defaultTitle,
  feedback_negativeLabel: state.languagePack.feedback_negativeLabel,
  feedback_positiveLabel: state.languagePack.feedback_positiveLabel,
  feedback_submitLabel: state.languagePack.feedback_submitLabel,
  fileSharing_fileIcon: state.languagePack.fileSharing_fileIcon,
});

/**
 * This component renders a specific message component based on a message's type.
 */
function MessageTypeComponent(props: MessageTypeComponentProps) {
  const {
    allowNewFeedback,
    hideFeedback,
    serviceManager,
    originalMessage,
    message,
  } = props;

  const { formatMessage } = useIntl();
  const languagePack = useSelector(selectMessageTypeStrings, shallowEqual);
  const feedbackDetailsRef = useRef<HTMLDivElement>(undefined);
  const chainOfThoughtRef = useRef<CDSAIChatChainOfThought>(null);
  const feedbackID = message.item.message_item_options?.feedback?.id;
  const chainOfThoughtPanelId = useUUID();
  const feedbackPanelID = useUUID();

  const feedbackHistory = isResponse(originalMessage)
    ? originalMessage.history?.feedback?.[feedbackID]
    : null;

  const feedbackInitialValues = useMemo<FeedbackInitialValues>(() => {
    if (!feedbackHistory) {
      return null;
    }
    return {
      text: feedbackHistory.text,
      selectedCategories: feedbackHistory.categories,
    };
  }, [feedbackHistory]);

  const [isChainOfThoughtOpen, setIsChainOfThoughtOpen] = useState(false);

  // Indicates if the one of the feedback details are open.
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  // Indicates if the negative or positive feedback buttons are marked as selected.
  const [isPositiveFeedbackSelected, setIsPositiveFeedbackSelected] = useState(
    feedbackHistory && feedbackHistory.is_positive
  );
  const [isNegativeFeedbackSelected, setIsNegativeFeedbackSelected] = useState(
    feedbackHistory && !feedbackHistory.is_positive
  );

  // Indicates if details have been submitted.
  const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(
    Boolean(feedbackHistory)
  );

  useEffect(() => {
    setIsChainOfThoughtOpen(false);
  }, [message.ui_state.id]);

  // Stable callback so children wrapped in React.memo (BodyMessageComponents,
  // CardItemComponent, GridItemComponent) can actually skip re-renders.
  const renderMessageComponent = useCallback(
    (childProps: MessageTypeComponentProps) => (
      <MessageTypeComponent {...childProps} />
    ),
    []
  );

  /**
   * Returns the appropriate component to render the given message.
   */
  function renderSpecificMessage(
    localMessageItem: LocalMessageItem,
    message: Message
  ) {
    if (isRequest(message)) {
      return renderRequest(localMessageItem, message as MessageRequest);
    }

    if (isResponse(message)) {
      const response = renderResponse(localMessageItem, message);
      const isResponseStopped =
        localMessageItem.item.streaming_metadata?.stream_stopped;
      return (
        <>
          {response}
          {isResponseStopped && <SystemMessage responseStopped />}
          {props.showChainOfThought &&
            renderChainOfThought(localMessageItem, message)}
          {renderFeedbackAndCustomFooter(localMessageItem, message)}
        </>
      );
    }

    return false;
  }

  /**
   * Returns the appropriate component to render the given message.
   */
  function renderRequest(
    localMessageItem: LocalMessageItem,
    originalMessage: MessageRequest
  ) {
    // A request's file attachments are rendered by MessageComponent, below the
    // bubble rather than inside it, so a request carrying only attachments renders
    // no bubble at all — hence the shared predicate rather than a local check.
    if (!hasRequestBubbleContent(localMessageItem, originalMessage)) {
      return null;
    }

    // If this was user entered text, show the user's original text before showing the text that was actually sent to
    // the assistant.
    const userText = getRequestBubbleText(localMessageItem, originalMessage);
    const displayContent = originalMessage.input.display_content;
    const isFile =
      originalMessage.input.message_type ===
      (InternalMessageRequestType.FILE as unknown as MessageInputType);

    return (
      <div className="cds-aichat--sent--text">
        {isFile && (
          <Attachment
            className="cds-aichat--sent-file-icon"
            aria-label={languagePack.fileSharing_fileIcon}
          />
        )}
        {/* The use of the heading role here is a compromise to enable the use of the
            next/previous heading hotkeys in JAWS to enable a screen reader user an easier ability to navigate
            messages. */}
        <div role="heading" aria-level={2}>
          {displayContent && !isFile ? (
            <MessageRichUserContent
              content={displayContent}
              message={originalMessage}
            />
          ) : (
            <MarkdownWithDefaults
              text={userText}
              removeHTML
              overrideSanitize={true}></MarkdownWithDefaults>
          )}
        </div>
      </div>
    );
  }

  /**
   * Returns the appropriate component to render the given message.
   */
  function renderResponse(
    localMessageItem: LocalMessageItem,
    message: MessageResponse
  ) {
    if (renderAsUserDefinedMessage(localMessageItem.item)) {
      // Render all invalid components as a user defined response
      return renderUserDefinedResponse(
        localMessageItem as LocalMessageItem<any>,
        message
      );
    }

    const responseType = localMessageItem.item.response_type;
    const withHuman = Boolean(
      message.message_options?.response_user_profile?.user_type ===
        UserType.HUMAN || localMessageItem.item.agent_message_type
    );
    switch (responseType) {
      case MessageResponseTypes.TEXT:
        return renderText(
          localMessageItem as LocalMessageItem<TextItem>,
          message,
          withHuman
        );
      case MessageResponseTypes.IMAGE:
        return renderImage(localMessageItem as LocalMessageItem<ImageItem>);
      case MessageResponseTypes.OPTION:
        return renderOption(
          localMessageItem as LocalMessageItem<OptionItem>,
          message
        );
      case MessageResponseTypes.CONNECT_TO_HUMAN_AGENT:
        return renderConnectToHumanAgent(
          localMessageItem as LocalMessageItem<ConnectToHumanAgentItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.INLINE_ERROR:
        return renderInlineError(
          localMessageItem as LocalMessageItem<InlineErrorItem>
        );
      case MessageResponseTypes.IFRAME:
        return renderIFrameMessage(
          localMessageItem as LocalMessageItem<IFrameItem>
        );
      case MessageResponseTypes.VIDEO:
        return renderVideoMessage(
          localMessageItem as LocalMessageItem<VideoItem>
        );
      case MessageResponseTypes.AUDIO:
        return renderAudioMessage(
          localMessageItem as LocalMessageItem<AudioItem>
        );
      case MessageResponseTypes.DATE:
        return renderDateMessage(
          localMessageItem as LocalMessageItem<DateItem>
        );
      case MessageResponseTypes.CONVERSATIONAL_SEARCH:
        return renderConversationalSearchMessage(
          localMessageItem as LocalMessageItem<ConversationalSearchItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.CARD:
        return renderCard(
          localMessageItem as LocalMessageItem<CardItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.CAROUSEL:
        return renderCarouselMessage(
          localMessageItem as LocalMessageItem<CarouselItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.BUTTON:
        return renderButtonItem(
          localMessageItem as LocalMessageItem<ButtonItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.GRID:
        return renderGrid(
          localMessageItem as LocalMessageItem<GridItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.PREVIEW_CARD:
        return renderPreviewCard(
          localMessageItem as LocalMessageItem<PreviewCardItem>,
          message as MessageResponse
        );
      case MessageResponseTypes.SYSTEM:
        return renderSystemMessage(
          localMessageItem as LocalMessageItem<SystemMessageItem>,
          message as MessageResponse
        );
      default:
        return renderUserDefinedResponse(
          localMessageItem as LocalMessageItem<TextItem>,
          message
        );
    }
  }

  function renderText(
    message: LocalMessageItem<TextItem>,
    originalMessage: MessageResponse,
    removeHTML: boolean
  ) {
    if (props.isNestedMessageItem) {
      return renderRichText(
        message,
        removeHTML,
        originalMessage as MessageResponse
      );
    }

    // For text provided by the assistant, pass it through some HTML formatting before displaying it.
    return (
      <div>
        {renderRichText(
          message,
          removeHTML,
          originalMessage as MessageResponse
        )}
      </div>
    );
  }

  function renderRichText(
    localMessageItem: LocalMessageItem<TextItem>,
    removeHTML: boolean,
    originalMessage?: MessageResponse
  ) {
    return (
      <MarkdownWithErrorHandling
        text={localMessageItem.item.text}
        streamingState={localMessageItem.ui_state.streamingState}
        isStreamingError={
          originalMessage?.history?.error_state ===
          MessageErrorState.FAILED_WHILE_STREAMING
        }
        removeHTML={removeHTML}
      />
    );
  }

  function renderOption(
    message: LocalMessageItem<OptionItem>,
    originalMessage: Message
  ) {
    const {
      requestInputFocus,
      serviceManager,
      disableUserInputs,
      isMessageForInput,
    } = props;
    const withHumanAgent = Boolean(message.item.agent_message_type);

    return (
      <OptionComponent
        localMessage={message}
        originalMessage={originalMessage}
        disableUserInputs={disableUserInputs || !isMessageForInput}
        requestInputFocus={requestInputFocus}
        serviceManager={serviceManager}
        removeHTML={withHumanAgent}
      />
    );
  }

  function renderImage(message: LocalMessageItem<ImageItem>) {
    return (
      <Image
        imageError={languagePack.errors_imageSource}
        source={message.item.source}
        title={message.item.title}
        description={message.item.description}
        altText={message.item.alt_text}
      />
    );
  }

  function renderInlineError(message: LocalMessageItem<TextItem>) {
    return <InlineError text={message.item.text} />;
  }

  function renderIFrameMessage(message: LocalMessageItem<IFrameItem>) {
    const { isNestedMessageItem } = props;
    const display = isNestedMessageItem
      ? IFrameItemDisplayOption.INLINE
      : undefined;

    return <IFrameMessage localMessage={message} displayOverride={display} />;
  }

  function renderVideoMessage(message: LocalMessageItem<VideoItem>) {
    const { item } = message;
    const { source, title, description, alt_text, file_accessibility } = item;
    return (
      <VideoComponent
        source={source}
        title={title}
        description={description}
        baseHeight={getMediaDimensions(item)?.base_height}
        ariaLabel={alt_text}
        subtitle_tracks={file_accessibility?.subtitle_tracks}
      />
    );
  }

  function renderAudioMessage(message: LocalMessageItem<AudioItem>) {
    const { source, title, description, alt_text, file_accessibility } =
      message.item;
    return (
      <AudioComponent
        source={source}
        title={title}
        description={description}
        ariaLabel={alt_text}
        transcript={file_accessibility?.transcript}
      />
    );
  }

  function renderDateMessage(message: LocalMessageItem<DateItem>) {
    return (
      <DatePickerComponent
        localMessage={message}
        disabled={!props.isMessageForInput}
        scrollElementIntoView={props.scrollElementIntoView}
      />
    );
  }

  function renderUserDefinedResponse(
    message: LocalMessageItem,
    originalMessage: MessageResponse
  ) {
    const { serviceManager } = props;
    return (
      <UserDefinedResponse
        isStreamingError={
          originalMessage?.history?.error_state ===
          MessageErrorState.FAILED_WHILE_STREAMING
        }
        localMessageID={message.ui_state.id}
        serviceManager={serviceManager}
      />
    );
  }

  function renderConnectToHumanAgent(
    message: LocalMessageItem,
    originalMessage: MessageResponse
  ) {
    const { serviceManager, disableUserInputs, isMessageForInput } = props;

    return (
      <ConnectToHumanAgent
        localMessage={message}
        originalMessage={originalMessage}
        serviceManager={serviceManager}
        disableUserInputs={disableUserInputs || !isMessageForInput}
        requestFocus={props.requestInputFocus}
      />
    );
  }

  function renderCard(
    message: LocalMessageItem<CardItem>,
    originalMessage: MessageResponse
  ) {
    const { isMessageForInput, requestInputFocus } = props;
    return (
      <CardItemComponent
        localMessageItem={message}
        fullMessage={originalMessage}
        isMessageForInput={isMessageForInput}
        requestFocus={requestInputFocus}
        renderMessageComponent={renderMessageComponent}
      />
    );
  }

  function renderPreviewCard(
    message: LocalMessageItem<PreviewCardItem>,
    originalMessage: MessageResponse
  ) {
    return (
      <PreviewCardComponent
        localMessageItem={message}
        fullMessage={originalMessage}
      />
    );
  }

  function renderConversationalSearchMessage(
    localMessageItem: LocalMessageItem<ConversationalSearchItem>,
    fullMessage: MessageResponse
  ) {
    const { scrollElementIntoView } = props;
    return (
      <ConversationalSearch
        localMessageItem={localMessageItem}
        scrollElementIntoView={scrollElementIntoView}
        isStreamingError={
          fullMessage?.history?.error_state ===
          MessageErrorState.FAILED_WHILE_STREAMING
        }
      />
    );
  }

  function renderButtonItem(
    message: LocalMessageItem<ButtonItem>,
    originalMessage: MessageResponse
  ) {
    const { isMessageForInput, requestInputFocus } = props;
    return (
      <ButtonItemComponent
        localMessageItem={message}
        fullMessage={originalMessage}
        isMessageForInput={isMessageForInput}
        requestFocus={requestInputFocus}
      />
    );
  }

  function renderCarouselMessage(
    message: LocalMessageItem<CarouselItem>,
    originalMessage: MessageResponse
  ) {
    const { isMessageForInput, requestInputFocus } = props;
    return (
      <div className="carousel-container">
        <Carousel
          nextBtnText={languagePack.carousel_nextNavButton}
          previousBtnText={languagePack.carousel_prevNavButton}>
          <div className="carousel-container-inner">
            <CarouselItemComponent
              localMessageItem={message}
              fullMessage={originalMessage}
              isMessageForInput={isMessageForInput}
              requestFocus={requestInputFocus}
              renderMessageComponent={renderMessageComponent}
            />
          </div>
        </Carousel>
      </div>
    );
  }

  function renderGrid(
    message: LocalMessageItem<GridItem>,
    originalMessage: MessageResponse
  ) {
    return (
      <GridItemComponent
        localMessageItem={message}
        originalMessage={originalMessage}
        renderMessageComponent={renderMessageComponent}
      />
    );
  }

  function renderSystemMessage(
    _localMessageItem: LocalMessageItem<SystemMessageItem>,
    message: MessageResponse
  ) {
    // Render inline system message (within message bubble)
    return <SystemMessage message={message} standalone={false} />;
  }

  function scrollChainOfThought(open: boolean, element: HTMLElement) {
    if (open) {
      setTimeout(() => {
        props.scrollElementIntoView(element, 64, 64);
      });
    }
  }

  function formatStepLabelText({
    stepNumber,
    stepTitle,
  }: {
    stepNumber: number;
    stepTitle: string;
  }) {
    return formatMessage(
      { id: 'chainOfThought_stepTitle' },
      { stepNumber, stepTitle }
    );
  }

  // Memoize markdown string functions for chain of thought
  /**
   * Renders chain of thought component for the given {@link MessageResponse}.
   */
  function renderChainOfThought(
    _localMessageItem: LocalMessageItem,
    message: MessageResponse
  ) {
    const chainOfThought = message.message_options?.chain_of_thought;
    if (!chainOfThought || props.isNestedMessageItem) {
      return false;
    }

    const handleToggle = (event: CustomEvent<{ open: boolean }>) => {
      const nextOpen = Boolean(event.detail?.open);
      setIsChainOfThoughtOpen(nextOpen);

      if (chainOfThoughtRef.current) {
        scrollChainOfThought(nextOpen, chainOfThoughtRef.current);
      }
    };

    const handleStepToggle = (event: CustomEvent<{ open: boolean }>) => {
      scrollChainOfThought(
        Boolean(event.detail?.open),
        (event.target as HTMLElement) ?? chainOfThoughtRef.current
      );
    };

    return (
      <div className="cds-aichat--received--chain-of-thought">
        <ChainOfThoughtToggle
          panelId={chainOfThoughtPanelId}
          open={isChainOfThoughtOpen}
          closedLabelText={languagePack.chainOfThought_explainabilityLabel}
          openLabelText={languagePack.chainOfThought_explainabilityLabel}
          onToggle={handleToggle}
        />
        <ChainOfThought
          id={chainOfThoughtPanelId}
          ref={chainOfThoughtRef}
          panelId={chainOfThoughtPanelId}
          open={isChainOfThoughtOpen}
          onToggle={handleToggle}
          onStepToggle={handleStepToggle}>
          {chainOfThought.map((step, index) => {
            const stepNumber = index + 1;
            const labelText = formatStepLabelText({
              stepNumber,
              stepTitle: step.title || step.tool_name || '',
            });
            const requestMarkdown = parseUnknownDataToMarkdown(
              step.request?.args
            );
            const responseMarkdown = parseUnknownDataToMarkdown(
              step.response?.content
            );

            return (
              <ChainOfThoughtStep
                key={step.title || step.tool_name || index}
                title={step.title || step.tool_name || ''}
                status={step.status || 'success'}
                stepNumber={stepNumber}
                labelText={labelText}
                statusSucceededLabelText={
                  languagePack.chainOfThought_statusSucceededLabel
                }
                statusFailedLabelText={
                  languagePack.chainOfThought_statusFailedLabel
                }
                statusProcessingLabelText={
                  languagePack.chainOfThought_statusProcessingLabel
                }>
                <ToolCallData
                  toolName={step.tool_name}
                  inputLabelText={languagePack.chainOfThought_inputLabel}
                  outputLabelText={languagePack.chainOfThought_outputLabel}
                  toolLabelText={languagePack.chainOfThought_toolLabel}>
                  {step.description ? (
                    <div slot="description">
                      <MarkdownWithDefaults text={step.description} />
                    </div>
                  ) : null}
                  {requestMarkdown ? (
                    <div slot="input">
                      <MarkdownWithDefaults text={requestMarkdown} />
                    </div>
                  ) : null}
                  {responseMarkdown ? (
                    <div slot="output">
                      <MarkdownWithDefaults text={responseMarkdown} />
                    </div>
                  ) : null}
                </ToolCallData>
              </ChainOfThoughtStep>
            );
          })}
        </ChainOfThought>
      </div>
    );
  }

  /**
   * Renders the feedback options for the given message item if appropriate.
   */
  function renderFeedback(
    localMessageItem: LocalMessageItem,
    message: MessageResponse
  ) {
    const feedbackOptions =
      localMessageItem.item.message_item_options?.feedback || {};

    const {
      id: feedbackID,
      is_on,
      max_length = 1000,
      show_positive_details = true,
      show_negative_details = true,
      show_text_area = true,
      show_prompt = true,
      title,
      prompt,
      categories,
      placeholder,
      disclaimer,
    } = feedbackOptions;

    if (
      props.isNestedMessageItem ||
      hideFeedback ||
      (!allowNewFeedback && !feedbackHistory) ||
      !is_on
    ) {
      return false;
    }

    /**
     * Updates the message history with the feedback data provided.
     */
    function updateFeedbackHistory(data: MessageHistoryFeedback) {
      if (feedbackID) {
        const history: MessageResponseHistory = {
          feedback: {
            [feedbackID]: data,
          },
        };
        serviceManager.store.dispatch(
          actions.mergeMessageHistory(localMessageItem.fullMessageID, history)
        );
      }
    }

    /**
     * Handles when one of the feedback buttons is clicked. We also treat clicking the cancel button the same way as
     * clicking the feedback button.
     */
    function onFeedbackClicked(isPositive: boolean) {
      const toggleToSelected = isPositive
        ? !isPositiveFeedbackSelected
        : !isNegativeFeedbackSelected;
      const openDetails =
        (isPositive ? show_positive_details : show_negative_details) &&
        toggleToSelected;

      if (toggleToSelected && !openDetails) {
        // If the button has been toggled to selected but we're not showing details, that means the option is considered
        // immediately submitted.
        updateFeedbackHistory({ is_positive: isPositive });
        setIsFeedbackSubmitted(true);

        serviceManager.fire({
          type: BusEventType.FEEDBACK,
          messageItem: localMessageItem.item,
          message,
          interactionType: FeedbackInteractionType.SUBMITTED,
          isPositive,
        } satisfies BusEventFeedback);
      } else {
        setIsFeedbackOpen(openDetails);
        if (openDetails) {
          setTimeout(() => {
            props.scrollElementIntoView(feedbackDetailsRef.current, 48, 56);
          });
        }

        serviceManager.fire({
          type: BusEventType.FEEDBACK,
          messageItem: localMessageItem.item,
          message,
          interactionType: openDetails
            ? FeedbackInteractionType.DETAILS_OPENED
            : FeedbackInteractionType.DETAILS_CLOSED,
          isPositive,
        } satisfies BusEventFeedback);
      }

      setIsPositiveFeedbackSelected(isPositive ? toggleToSelected : false);
      setIsNegativeFeedbackSelected(isPositive ? false : toggleToSelected);
    }

    /**
     * Handles when the feedback submit button is clicked.
     */
    function onSubmit(isPositive: boolean, details: FeedbackSubmitDetails) {
      setIsFeedbackSubmitted(true);
      setIsFeedbackOpen(false);

      const event: BusEventFeedback = {
        type: BusEventType.FEEDBACK,
        messageItem: localMessageItem.item,
        message,
        interactionType: FeedbackInteractionType.SUBMITTED,
        isPositive,
        text: details.text,
        categories: details.selectedCategories,
      };
      serviceManager.fire(event);

      // Submit this update to be recorded in history.
      updateFeedbackHistory({
        is_positive: event.isPositive,
        text: event.text,
        categories: event.categories,
      });
    }

    /**
     * Render the feedback popup for either positive or negative feedback.
     */
    function renderFeedbackPopup(isPositive: boolean) {
      // Only one popup can be open and which one is opened depends on which feedback is selected.
      const isOpen =
        isFeedbackOpen &&
        (isPositive ? isPositiveFeedbackSelected : isNegativeFeedbackSelected);

      let filteredCategories;
      // Categories can be an array of strings or an object with positive and negative arrays.
      if (Array.isArray(categories)) {
        filteredCategories = categories;
      } else if (isPositive) {
        filteredCategories = categories?.positive;
      } else {
        filteredCategories = categories?.negative;
      }

      return (
        <Feedback
          class={`${prefix}--feedback-details-${
            isPositive ? 'positive' : 'negative'
          }`}
          id={`${feedbackPanelID}-feedback-${
            isPositive ? 'positive' : 'negative'
          }`}
          isOpen={isOpen}
          isReadonly={isFeedbackSubmitted}
          maxLength={max_length}
          onClose={() => onFeedbackClicked(isPositive)}
          onSubmit={(event: CustomEvent<FeedbackSubmitDetails>) =>
            onSubmit(isPositive, event.detail)
          }
          initialValues={
            feedbackHistory && feedbackHistory?.is_positive === isPositive
              ? feedbackInitialValues
              : null
          }
          showTextArea={show_text_area}
          showBody={show_prompt}
          title={title || languagePack.feedback_defaultTitle}
          body={prompt || languagePack.feedback_defaultPrompt}
          categories={filteredCategories}
          categoriesLabel={languagePack.feedback_categoriesLabel}
          placeholder={placeholder || languagePack.feedback_defaultPlaceholder}
          disclaimer={disclaimer}
          primaryLabel={languagePack.feedback_submitLabel}
        />
      );
    }

    return {
      buttons: (
        <FeedbackButtons
          isPositiveOpen={isFeedbackOpen && isPositiveFeedbackSelected}
          isNegativeOpen={isFeedbackOpen && isNegativeFeedbackSelected}
          isPositiveSelected={isPositiveFeedbackSelected}
          isNegativeSelected={isNegativeFeedbackSelected}
          hasPositiveDetails={show_positive_details}
          hasNegativeDetails={show_negative_details}
          isPositiveDisabled={isNegativeFeedbackSelected || isFeedbackSubmitted}
          isNegativeDisabled={isPositiveFeedbackSelected || isFeedbackSubmitted}
          positiveLabel={languagePack.feedback_positiveLabel}
          negativeLabel={languagePack.feedback_negativeLabel}
          panelID={feedbackPanelID}
          onClick={(event: CustomEvent<{ isPositive: boolean }>) =>
            onFeedbackClicked(event.detail.isPositive)
          }
        />
      ),
      details: (
        <div ref={feedbackDetailsRef}>
          {renderFeedbackPopup(true)}
          {renderFeedbackPopup(false)}
        </div>
      ),
    };
  }

  /**
   * Renders the custom footer slot for the given message item if appropriate.
   */
  function renderCustomFooter(localMessageItem: LocalMessageItem) {
    const footerOptions =
      localMessageItem.item.message_item_options?.custom_footer_slot;

    if (
      props.isNestedMessageItem ||
      !footerOptions ||
      footerOptions.is_on === false
    ) {
      return false;
    }

    return <CustomFooterSlot footerOptions={footerOptions} />;
  }

  /**
   * Renders both feedback buttons and custom footer in the same container.
   */
  function renderFeedbackAndCustomFooter(
    localMessageItem: LocalMessageItem,
    message: MessageResponse
  ) {
    const feedback = renderFeedback(localMessageItem, message);
    const customFooter = renderCustomFooter(localMessageItem);

    // If neither feedback nor custom footer should be rendered, return false
    if (!feedback && !customFooter) {
      return false;
    }

    // Render both in the same feedback container div
    return (
      <div className="cds-aichat--received--feedback">
        <div className="cds-aichat--message-footer">
          {feedback && feedback.buttons}
          {customFooter}
        </div>
        {feedback && feedback.details}
      </div>
    );
  }

  return renderSpecificMessage(props.message, props.originalMessage);
}

export { MessageTypeComponent };
const Attachment = carbonIconToReact(Attachment16);
